import React, { memo, useCallback } from "react";
import { Box, MainText, PressableOpacity } from "app/components/common";
import { FlatList } from "react-native";
import { CustomBubble } from "./Components/CustomBubble";
import { useSelector } from "react-redux";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import i18n from "@microdoing/shared/i18n";
import Animated, {
  useAnimatedKeyboard,
  useAnimatedStyle,
  withTiming,
} from "react-native-reanimated";
import { isAndroid } from "@microdoing/shared/utils/deviceDetectors";
import {
  getMessagesByIds,
  getMessagesIds,
} from "@microdoing/shared/services/message/selectors";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const ReanimatedBox = Animated.createAnimatedComponent(Box);

const styles = {
  scrollContent: { paddingTop: 0, paddingBottom: 20 },
};

interface Props {
  messages?: any;
  handlePressAvatar: () => void;
  setFocusedMessage: (message: any) => void;
  displayUnsend: () => void;
  onLoadEarlier: () => void;
  requesting: boolean;
  isReviewChat?: boolean;
}

const Chat = ({
  messages,
  isReviewChat = false,
  handlePressAvatar,
  setFocusedMessage,
  displayUnsend,
  onLoadEarlier,
  requesting,
}: Props) => {
  const language = useSelector(getLanguage);
  const messagesByIds = useSelector(getMessagesByIds);
  const messagesIds = useSelector(getMessagesIds);

  const isLoadEarlierVisible =
    messages.length % 20 === 0 && !requesting && messages.length > 0;

  const keyboard = useAnimatedKeyboard();

  const insets = useSafeAreaInsets();

  const animatedBoxStyle = useAnimatedStyle(() => {
    return {
      height: keyboard.height.value + 25,
    };
  }, []);

  const bottomAnimatedStyle = useAnimatedStyle(() => {
    return {
      height: keyboard.height.value
        ? !!insets.bottom
          ? 20
          : 50
        : withTiming(50),
    };
  }, []);

  const reanimatedBoxStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: -keyboard.height.value }],
    };
  });

  const handleKeyExtractor = useCallback((item: any, index: number) => {
    return isReviewChat ? item?._id : item;
  }, []);

  const renderReviewChatItem = useCallback(
    ({ item, index }) => {
      const currentMessage = item;
      const nextMessage =
        index + 1 < messages.length ? messages[index + 1] : {};

      return (
        <CustomBubble
          handlePressAvatar={handlePressAvatar}
          nextMessage={nextMessage}
          currentMessage={currentMessage}
          setFocusedMessage={setFocusedMessage}
          displayUnsend={displayUnsend}
        />
      );
    },
    [messages],
  );

  const renderItem = useCallback(
    ({ item, index }) => {
      const currentMessage = messagesByIds[item];
      const nextMessage =
        index + 1 < messagesIds.length
          ? messagesByIds[messagesIds[index + 1]]
          : {};

      return (
        <CustomBubble
          handlePressAvatar={handlePressAvatar}
          nextMessage={nextMessage}
          currentMessage={currentMessage}
          setFocusedMessage={setFocusedMessage}
          displayUnsend={displayUnsend}
        />
      );
    },
    [messagesByIds, messagesIds],
  );

  return (
    <Box flex={1} overflow="hidden">
      <ReanimatedBox flex={1} style={reanimatedBoxStyle}>
        <FlatList
          nativeID="chat-list"
          inverted
          keyboardShouldPersistTaps="handled"
          ListFooterComponent={
            <ReanimatedBox
              style={animatedBoxStyle}
              alignItems="center"
              justifyContent="flex-end"
            >
              {isLoadEarlierVisible && (
                <PressableOpacity onPress={onLoadEarlier}>
                  <Box
                    paddingVertical="xxs"
                    paddingHorizontal="s"
                    backgroundColor="neutral.100"
                    alignItems="center"
                    justifyContent="center"
                    alignSelf="center"
                    width="auto"
                    borderRadius={4}
                  >
                    <MainText variant="l3-regular">
                      {i18n.t("load-earlier-messages", { lng: language })}
                    </MainText>
                  </Box>
                </PressableOpacity>
              )}
            </ReanimatedBox>
          }
          ListHeaderComponent={<ReanimatedBox style={bottomAnimatedStyle} />}
          contentContainerStyle={styles.scrollContent}
          keyExtractor={handleKeyExtractor}
          data={isReviewChat ? [...messages].reverse() : messagesIds}
          renderItem={isReviewChat ? renderReviewChatItem : renderItem}
        />
      </ReanimatedBox>
    </Box>
  );
};

export default memo(Chat);
